import { create } from 'zustand';
import { Workflow, WorkflowNode, WorkflowEdge } from '../types';

interface WorkflowState {
  workflows: Workflow[];
  currentWorkflow: Workflow | null;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  selectedNode: WorkflowNode | null;
  isDirty: boolean;
}

interface WorkflowActions {
  setWorkflows: (workflows: Workflow[]) => void;
  setCurrentWorkflow: (workflow: Workflow | null) => void;
  setNodes: (nodes: WorkflowNode[]) => void;
  setEdges: (edges: WorkflowEdge[]) => void;
  addNode: (node: WorkflowNode) => void;
  updateNode: (nodeId: string, data: Partial<WorkflowNode>) => void;
  removeNode: (nodeId: string) => void;
  addEdge: (edge: WorkflowEdge) => void;
  removeEdge: (edgeId: string) => void;
  setSelectedNode: (node: WorkflowNode | null) => void;
  setDirty: (isDirty: boolean) => void;
  resetWorkflow: () => void;
}

type WorkflowStore = WorkflowState & WorkflowActions;

const initialState: WorkflowState = {
  workflows: [],
  currentWorkflow: null,
  nodes: [],
  edges: [],
  selectedNode: null,
  isDirty: false,
};

export const useWorkflowStore = create<WorkflowStore>((set, get) => ({
  ...initialState,

  setWorkflows: (workflows) => set({ workflows }),

  setCurrentWorkflow: (workflow) => {
    if (workflow) {
      set({
        currentWorkflow: workflow,
        nodes: workflow.flowData.nodes,
        edges: workflow.flowData.edges,
        isDirty: false,
      });
    } else {
      set({
        currentWorkflow: null,
        nodes: [],
        edges: [],
        isDirty: false,
      });
    }
  },

  setNodes: (nodes) => set({ nodes, isDirty: true }),

  setEdges: (edges) => set({ edges, isDirty: true }),

  addNode: (node) =>
    set((state) => ({
      nodes: [...state.nodes, node],
      isDirty: true,
    })),

  updateNode: (nodeId, data) =>
    set((state) => ({
      nodes: state.nodes.map((node) =>
        node.id === nodeId ? { ...node, ...data } : node
      ),
      isDirty: true,
    })),

  removeNode: (nodeId) =>
    set((state) => ({
      nodes: state.nodes.filter((node) => node.id !== nodeId),
      edges: state.edges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      ),
      isDirty: true,
    })),

  addEdge: (edge) =>
    set((state) => ({
      edges: [...state.edges, edge],
      isDirty: true,
    })),

  removeEdge: (edgeId) =>
    set((state) => ({
      edges: state.edges.filter((edge) => edge.id !== edgeId),
      isDirty: true,
    })),

  setSelectedNode: (node) => set({ selectedNode: node }),

  setDirty: (isDirty) => set({ isDirty }),

  resetWorkflow: () => set(initialState),
}));
